/**
 * V6-010 砺锋台 V2 — 勋章 SVG 渲染
 *
 * 复用 1 套 K1-K12 徽章 + 类型标识
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §10
 * @date 2026-07-19
 */

import type { BattleType } from "./types";
import { TYPE_COLORS, TYPE_LABELS } from "./types";
import { levelToMedalIndex } from "./level-system";

// ============================================================================
// K1-K12 勋章 SVG（内嵌，避免依赖 data/medals/ 文件加载）
// ============================================================================

/**
 * 渲染勋章 SVG
 *
 * 设计：六边形外框 + 中心星形 + 等级数字
 * 颜色随 K1-K12 渐变（青铜→银→金→虹光）
 */
export function renderMedalSvg(level: number, type: BattleType): string {
  const medalIdx = levelToMedalIndex(level);  // 0-11
  const color = TYPE_COLORS[type];
  const isLocked = type === "large";

  // K1-K12 颜色梯度
  const medalColors = [
    "#8B6F47",  // K1 青铜
    "#A07845",  // K2
    "#B8895A",  // K3
    "#C9A567",  // K4
    "#D4B370",  // K5
    "#C0C0C0",  // K6 银
    "#D8D8D8",  // K7
    "#E8E8E8",  // K8
    "#FFD700",  // K9 金
    "#FFC700",  // K10
    "#FFB700",  // K11
    "#FFA500",  // K12 虹光
  ];
  const medalColor = isLocked ? "#3A4555" : medalColors[medalIdx];

  // 六边形顶点（viewBox 100×100，中心 50,50，半径 40）
  const hexPoints = "50,10 86,30 86,70 50,90 14,70 14,30";

  // 中心星形（5 角星，半径 20）
  const starPoints = starShape(50, 50, 20, 10, 5);

  // 等级数字
  const levelText = isLocked ? "🔒" : `Lv.${level}`;

  return `<svg viewBox="0 0 100 100" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <!-- 外框六边形 -->
    <polygon points="${hexPoints}" fill="none" stroke="${medalColor}" stroke-width="2" />
    <!-- 内层六边形（装饰） -->
    <polygon points="${hexPoints}" fill="${medalColor}" fill-opacity="0.15" />
    <!-- 中心星形 -->
    <polygon points="${starPoints}" fill="${medalColor}" fill-opacity="0.7" stroke="${medalColor}" stroke-width="1" />
    <!-- 类型标识色环（底部） -->
    <rect x="30" y="78" width="40" height="10" fill="${color}" fill-opacity="0.8" />
    <text x="50" y="86" text-anchor="middle" fill="#06080E" font-family="monospace" font-size="8" font-weight="bold">${type}</text>
    <!-- 等级数字（顶部） -->
    <text x="50" y="22" text-anchor="middle" fill="${isLocked ? "#6B7889" : medalColor}" font-family="monospace" font-size="9" font-weight="bold">${levelText}</text>
  </svg>`;
}

/** 生成五角星顶点 */
function starShape(cx: number, cy: number, outerR: number, innerR: number, points: number): string {
  const coords: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = (Math.PI / points) * i - Math.PI / 2;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    coords.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return coords.join(" ");
}

/** 渲染类型卡片（含勋章 + 等级 + 经验条 + 标签） */
export function renderTypeCard(
  type: BattleType,
  level: number,
  expInCurrentLevel: number,
  expNeededForNext: number,
  isSelected: boolean,
  isLocked: boolean,
  clearedCount: number,
  totalLevels: number,
  rankText: string
): string {
  const color = TYPE_COLORS[type];
  const label = TYPE_LABELS[type];
  const selectedClass = isSelected ? " s10-card--selected" : "";
  const lockedClass = isLocked ? " s10-card--locked" : "";

  const expProgress = expNeededForNext > 0 ? (expInCurrentLevel / expNeededForNext) : 0;
  const expPct = Math.min(100, Math.max(0, expProgress * 100));

  return `<div class="s10-card${selectedClass}${lockedClass}" data-type="${type}" style="--card-color: ${color};">
    <div class="s10-card-medal">
      ${renderMedalSvg(isLocked ? 0 : level, type)}
    </div>
    <div class="s10-card-info">
      <div class="s10-card-label">${label}</div>
      ${isLocked
        ? `<div class="s10-card-locked-text">暂未开放</div>`
        : `<div class="s10-card-level">Lv.${level}</div>
           <div class="s10-card-exp-bar">
             <div class="s10-card-exp-fill" style="width: ${expPct}%;"></div>
           </div>
           <div class="s10-card-exp-text">${expInCurrentLevel}/${expNeededForNext} EXP</div>
           <div class="s10-card-stats">
             <span>通关 ${clearedCount}/${totalLevels}</span>
             <span class="s10-card-rank">${rankText}</span>
           </div>`
      }
    </div>
  </div>`;
}
